import type { PageVariant } from "~/core/api/index";
import type { Check } from "./fit";

export type Tone = "neutral" | "success" | "warning" | "accent";

/** Where one platform stands, and the step it is waiting on. */
export interface PlatformStatus {
	/** The step: nothing yet, a version to review, a task to send, sent. */
	stage: "none" | "review" | "send" | "sent";
	tone: Tone;
	label: [string, string];
	/** What to fix before this platform can go, the checks' and the server's. */
	problems: [string, string][];
	/** The post itself, once one exists. */
	link?: string;
}

/**
 * One platform's state. Before there is a version, what the article on
 * screen would run into; once there is one, what the version itself was
 * found to lack — it is what goes, and it may have been edited since. What
 * the server already handled, like a body cut to the platform's limit, is
 * not one: a problem is something still to do. The
 * row and the detail both draw from this, so they cannot disagree about
 * whether a platform is ready.
 */
export function platformStatus(
	checks: Check[],
	variant: PageVariant | undefined,
	issueText: (issue: string) => [string, string],
	/** A publishing task made for this page and platform, from here or the distribution screen. */
	task?: { state: string; published_url?: string | null },
): PlatformStatus {
	const state = task?.state ?? (variant?.task_id ? variant.state : undefined);
	if (state !== undefined) {
		const sent = state === "published" || state === "user_reported";
		return {
			stage: sent ? "sent" : "send",
			tone: sent ? "success" : state === "failed" || state === "unknown" ? "warning" : "accent",
			label: sent ? ["Published", "已发布"] : ["Ready to send", "待发布"],
			problems: [],
			link: (sent && task?.published_url) || undefined,
		};
	}
	const problems: [string, string][] = variant
		? variant.issues.filter((i) => i !== "media_spec_review" && !i.startsWith("shortened:")).map(issueText)
		: checks.filter((c) => c.tone === "fail").map((c) => c.text);
	if (variant)
		return {
			stage: "review",
			tone: problems.length ? "warning" : "accent",
			label: problems.length
				? [`${problems.length} to fix`, `${problems.length} 项待处理`]
				: ["Ready to approve", "待确认"],
			problems,
		};
	return {
		stage: "none",
		tone: problems.length ? "warning" : "neutral",
		label: problems.length
			? [`${problems.length} to fix`, `${problems.length} 项不符合`]
			: ["No version yet", "未生成版本"],
		problems,
	};
}
